import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
    SCE_MODULES,
    Module,
    MatchRule,
    ScanExclusion,
    ComputedAccessibilityCheck,
} from '../types/scan-exclusions.type';
import { ActiveFeatureService } from '@monsido/ng2/services/active-feature/active-feature.service';
import { AccessibilityScanExclusionsRepo } from './accessibility-scan-exclusions.repo';
import { ScanExclusionsRepo } from './scan-exclusions.repo';
import { Domain } from '@monsido/ng2/modules/models/api/domain';
import { DomainRepoService } from '@client/app/services/api/domain-repo/domain-repo.service';
import { GlobalAccessibilityChecksRepoService } from '@client/app/services/accessibility-checks-repo/GlobalAccessibilityChecksRepo.service';

export type ComputedDomain = {
    id: number;
    title: string;
    url: string;
};

@Injectable()
export class ScanExclusionsService {
    private readonly ACCESSIBILITY_CHECKS_PAGE_SIZE = 300;
    constructor (
        private readonly translocoService: TranslocoService,
        private readonly activeFeatureService: ActiveFeatureService,
        private readonly accessibilityScanExclusionsRepo: AccessibilityScanExclusionsRepo,
        private readonly scanExclusionsRepo: ScanExclusionsRepo,
        private readonly domainRepoService: DomainRepoService,
        private readonly globalAccessibilityChecksRepoService: GlobalAccessibilityChecksRepoService,
    ) { }

    getModules (): Module[] {
        return [
            {
                name: this.translocoService.translate('adminScanExclusions.accessibility.module'),
                value: SCE_MODULES.accessibility,
                hasAccess: this.activeFeatureService.isFeatureActive(
                    'admin_accessibility',
                ),
            },
            {
                name: this.translocoService.translate('adminScanExclusions.misspellings.module'),
                value: SCE_MODULES.misspelling,
                hasAccess: this.activeFeatureService.isFeatureActive('admin_source_code_excludes') &&
                this.activeFeatureService.isFeatureActive('qa_spellcheck'),
            },
            {
                name: this.translocoService.translate('adminScanExclusions.languageMatch.module'),
                value: SCE_MODULES.languageMatch,
                hasAccess: this.activeFeatureService.isFeatureActive('admin_source_code_excludes') &&
                this.activeFeatureService.isFeatureActive('language_match'),
            },
            {
                name: this.translocoService.translate('adminScanExclusions.readability.module'),
                value: SCE_MODULES.readability,
                hasAccess: this.activeFeatureService.isFeatureActive('admin_source_code_excludes') &&
                this.activeFeatureService.isFeatureActive('admin_readability'),
            },
        ].filter((module) => module.hasAccess);
    }

    getMatchRuleOptions (): MatchRule[] {
        return [
            {
                name: 'Exact',
                value: 'exact',
            },
            {
                name: 'Regex',
                value: 'regex',
            },
            {
                name: 'Contains',
                value: 'contains',
            },
        ];
    }

    getDefaultModule () {
        const module = this.getModules();
        return module[0]?.value;
    }

    async fetchExclusionData (id: number, module: string): Promise<ScanExclusion> {
        if (module === 'accessibility') {
            return await this.accessibilityScanExclusionsRepo.getAccessibilityScanExclusions(id);
        } else {
            return await this.scanExclusionsRepo.getScanExclusions(id);
        }
    }

    async fetchDomains (searchText: string): Promise<ComputedDomain[]> {
        const domainsResponse = await this.domainRepoService.getAll({
            page: 1,
            page_size: 20,
            search: searchText,
        });
        return domainsResponse.map((domain: Domain) => ({
            id: domain.id,
            title: domain.title,
            url: domain.url,
        }));
    }

    async fetchAccessibilityChecks (): Promise<ComputedAccessibilityCheck[]> {
        const checksResponse = await this.globalAccessibilityChecksRepoService.getAll({ page_size: this.ACCESSIBILITY_CHECKS_PAGE_SIZE });
        return checksResponse.map((check: ComputedAccessibilityCheck) => ({
            id: check.id,
            name: check.name,
        }));
    }
}
